import { useCallback } from 'react';

import { getAppStore, useAppStore } from '@/store';
import copyToClipboard from '@/utils/file/copyToClipboard';
import createRemoteSettingHook from '@/utils/user/createRemoteSettingHook';
import parsePresetAttributes from '@/utils/file/parsePresetAttributes';
import openExportShare, {
  BASE_EXCLUDED_SOCIALS,
  RESULT_INCLUDED_SOCIALS,
  ShareTouchpoints,
} from '@/utils/editor/openExportShare';
import type { ExternalApp } from '@/utils/editor/openExportShare';
import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import {
  type ScreenType,
  AiGenScreenAction,
  AnalyticsSettings,
} from '@/types/analytics';

import type { IAppStore } from '@/store';

const selector = (store: IAppStore) => ({
  showToast: store.showToast,
});

type PostToPicsartMiniappConfig = {
  isVideoPostingEnabled: boolean;
};

const POST_TO_PICSART_REMOTE_SETTING_NAME = 'post_to_picsart_miniapp_config';
const DEFAULT_POST_TO_PICSART_CONFIG: PostToPicsartMiniappConfig = {
  isVideoPostingEnabled: false,
};

/**
 * Non-React accessor only — nothing renders off this config, so the factory's
 * `useSetting` goes unused here. Using `getSetting` rather than caching locally
 * dedupes concurrent callers: `cachedValue ??= await fetch()` assigns only
 * *after* awaiting, so two shares fired before the first resolved each issued
 * their own SDK request.
 */
const { getSetting: getPostToPicsartConfig } = createRemoteSettingHook({
  name: POST_TO_PICSART_REMOTE_SETTING_NAME,
  defaultValue: DEFAULT_POST_TO_PICSART_CONFIG,
  validate: () => true,
  select: (settings) => settings,
});

/**
 * App metadata the post-to-Picsart channel needs to credit the originating
 * preset. The slug lives on the Drive file's `category` attribute — see the
 * Drive attributes workaround in the API reference.
 */
const buildPostToPicsartApp = (driveUid: string): ExternalApp => {
  const sharedContentFile = getAppStore().contentFiles.find(
    (file) => file.uuid === driveUid
  );
  const { slug } = parsePresetAttributes(
    sharedContentFile?.attributes?.category
  );

  return {
    id: MINIAPP_PACKAGE_ID,
    type: 'MINIAPP',
    version: import.meta.env.VITE_APP_VERSION ?? '',
    payload: { presetId: slug ?? '' },
  };
};

type ShareTarget = {
  url: string;
  driveUid?: string;
};

type UseShareFlowOptions = {
  screenType: ScreenType;
};

/** Shares a **generated file**. Presets share a link — see `usePresetShare`. */
const useShareFlow = ({ screenType }: UseShareFlowOptions) => {
  const { showToast } = useAppStore(selector);

  const handleShare = useCallback(
    async (target: ShareTarget, analyticsSettings: AnalyticsSettings) => {
      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.SHARE,
        screen_type: screenType,
        settings: analyticsSettings,
      });

      if (target.driveUid) {
        try {
          const { isVideoPostingEnabled } = await getPostToPicsartConfig();

          await openExportShare({
            externals: [
              {
                url: target.url,
                fileUid: target.driveUid,
                aiGenerated: true,
                ...(isVideoPostingEnabled && {
                  app: buildPostToPicsartApp(target.driveUid),
                }),
              },
            ],
            includedSocials: RESULT_INCLUDED_SOCIALS,
            excludedSocials: isVideoPostingEnabled
              ? BASE_EXCLUDED_SOCIALS
              : [...BASE_EXCLUDED_SOCIALS, ShareTouchpoints.PostToPicsart],
          });
          return;
        } catch {
          // Fall through to clipboard fallback
        }
      }

      const success = await copyToClipboard(target.url);
      if (success) showToast('URL copied to clipboard');
    },
    [screenType, showToast]
  );

  return { handleShare };
};

export default useShareFlow;
